import moment from "moment";
import PropTypes from "prop-types";
import Dialog from "../Dialog";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";
import Progress from "../Progress";
import MUIButton from "@mui/material/Button";
import MUITableContainer from "@mui/material/TableContainer";
import MUITable from "@mui/material/Table";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUITableBody from "@mui/material/TableBody";
import MUIPaper from "@mui/material/Paper";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIAlert from "@mui/material/Alert";
import MUIBox from "@mui/material/Box";
import MUIIconButton from "@mui/material/IconButton";
import MUICopyIcon from "@mui/icons-material/ContentCopy";
import MUIStack from "@mui/material/Stack";
import ProductType from "../../Utility/Crud/ProductType";
import EmptyState from "../EmptyState";
import MUITooltip from "@mui/material/Tooltip";
import MUICloseIcon from "@mui/icons-material/Close";
import MUIInfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MainContext from "../../Context/MainContext/MainContext";
import MUICheckbox from "@mui/material/Checkbox";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

function hasQuoteRowQuantityOverride(row) {
  if (!(row?.productSubType?.defaultQuantity > 0)) {
    return false;
  }

  return row.productSubType.defaultQuantity !== row.originalQuantity;
}

export default class DialogUpdateInvoice extends Dialog {
  static contextType = MainContext; // For legacy class components

  constructor(props) {
    super(props);

    this.state = {
      rows: [],
      processingTime: 0,
      showSource: false,
      progress: 1,
      aiMessageIndex: 0,
      aiMessageOrder: [],
      overwriteExisting: false,
    };
    this.progressTimer = null;
    this.aiMessageTimer = null;
    this.aiMessages = [
      "Adjusting the dilithium crystals...",
      "Aligning ones and zeroes...",
      "Analyzing with machine precision...",
      "Asking Clippy for advice...",
      "Asking the big questions...",
      "Brains are better than brawn - this takes a second...",
      "Calculating with care...",
      "Consulting the wisdom of the cloud...",
      "Crafting the perfect response...",
      "Crunching the data...",
      "Data's still percolating...",
      "Deep in the data mines...",
      "Doing AI things. Beep boop...",
      "Doing the heavy lifting for you...",
      "Don't worry - we're on it...",
      "Double-checking everything...",
      "Fine-tuning the results...",
      "In the AI lab, mixing code and logic...",
      "Intelligence takes time...",
      "Just giving it one more pass...",
      "Just making it awesome for you...",
      "Loading up neural networks...",
      "Measuring twice, generating once...",
      "Optimizing every pixel...",
      "Our AI is making sure it gets it right...",
      "Plotting the best course forward...",
      "Polishing the final details...",
      "Precision takes patience...",
      "Proofreading the AI's thoughts...",
      "Reticulating splines...",
      "Rolling for initiative...",
      "Smarter than it looks; slower than you'd hope...",
      "Sorting signals from noise...",
      "Still computing - hang tight...",
      "Still thinking - AI can be a perfectionist...",
      "Still working...just takes a moment...",
      "Taming some unruly algorithms...",
      "Teaching the AI some new tricks...",
      "The robots haven't taken over - yet...",
      "Thinking really hard about this one...",
      "This may take up to a minute - we promise it's worth it...",
      "This part takes a little brainpower...",
      "Turning complexity into clarity...",
      "Wrangling complex logic...",
      "Your results are in good hands...",
    ];
  }

  // Fisher-Yates shuffle
  getRandomOrder = () => {
    const arr = Array.from({ length: this.aiMessages.length }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  componentDidUpdate(prevProps) {
    if (prevProps.uploadQuoteData !== this.props.uploadQuoteData) {
      const rawRows = this.props.uploadQuoteData?.attributes?.line_items || [];
      const filteredRows = rawRows
        .filter((row) => {
          const hasPrice = row.price != null && parseFloat(row.price) >= 200;
          const hasHcpcs = row.hcpcs && row.hcpcs.trim() !== "";
          return hasPrice || hasHcpcs;
        })
        .map((row) => ({
          ...row,
          originalQuantity: row.qty || 1,
          qty: row.qty || 1,
        }));

      this.setState({
        rows: filteredRows,
        processingTime:
          this.props.uploadQuoteData?.metrics?.[
            "timing.total.processing.quote.duration"
          ],
      });
    }

    // Start/stop progress timer based on isLoading
    if (!prevProps.isLoading && this.props.isLoading) {
      this.startProgressTimer();
    } else if (prevProps.isLoading && !this.props.isLoading) {
      this.stopProgressTimer();
      this.setState({ progress: 100 });
    }

    // AI message cycling logic
    if (!prevProps.isLoading && this.props.isLoading) {
      this.startAIMessageTimer();
    } else if (prevProps.isLoading && !this.props.isLoading) {
      this.stopAIMessageTimer();
      this.setState({ aiMessageIndex: 0, aiMessageOrder: [] });
    }
  }

  componentWillUnmount() {
    this.stopProgressTimer();
    this.stopAIMessageTimer();
  }

  startProgressTimer = (totalDuration = 50000) => {
    // 50 seconds total before final crawl
    this.stopProgressTimer();
    this.setState({ progress: 1 });

    const tailStart = 94; // start final crawl at 94%
    const tailRateMsPerPercent = 5000; // 1% every 5 seconds
    const tailDuration = (100 - tailStart) * tailRateMsPerPercent; // 30 seconds
    const totalRunTime = totalDuration + tailDuration; // ≈ 80 seconds total visible time
    const startTime = Date.now();

    this.progressTimer = setInterval(() => {
      this.setState((prev) => {
        let { progress } = prev;
        const elapsed = Date.now() - startTime;

        if (elapsed >= totalRunTime || progress >= 100) {
          clearInterval(this.progressTimer);
          return { progress: 100 };
        }

        // --- Phase 1: Fast (1 → 65%) ---
        if (elapsed < totalDuration * 0.55) {
          // first ~27.5 seconds
          const increment =
            (65 - progress) / (((totalDuration * 0.55) / 1000) * 10);
          return {
            progress: Math.min(progress + Math.max(0.15, increment), 65),
          };
        }

        // --- Phase 2: Medium (65 → 94%) ---
        if (elapsed < totalDuration) {
          // remaining ~22.5 seconds
          const increment =
            (94 - progress) / (((totalDuration * 0.45) / 1000) * 10);
          return {
            progress: Math.min(progress + Math.max(0.05, increment), 94),
          };
        }

        // --- Phase 3: Final crawl (94 → 100%, 1% / 5 seconds) ---
        const crawlIncrement = 1 / (tailRateMsPerPercent / 100); // 0.02% per 100 milliseconds tick
        return { progress: Math.min(progress + crawlIncrement, 100) };
      });
    }, 100);
  };

  stopProgressTimer = () => {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  };

  startAIMessageTimer = () => {
    this.stopAIMessageTimer();
    this.setState({ aiMessageIndex: 0, aiMessageOrder: this.getRandomOrder() });
    this.aiMessageTimer = setInterval(() => {
      this.setState((prev) => ({
        aiMessageIndex: (prev.aiMessageIndex + 1) % this.aiMessages.length,
      }));
    }, 10000); // 10 seconds per message
  };

  stopAIMessageTimer = () => {
    if (this.aiMessageTimer) {
      clearInterval(this.aiMessageTimer);
      this.aiMessageTimer = null;
    }
  };

  /**
   * Render header title of dialog
   */
  renderHeader() {
    return (
      <>
        <MUITypography variant="h6" color="textPrimary">
          Upload Quote
        </MUITypography>
      </>
    );
  }

  /**
   * Override the parent Dialog's renderCloseIcon() method to return null.
   * This removes the close icon from this specific dialog since we don't want
   * users to be able to close it while a quote is being processed.
   */
  renderCloseIcon() {
    if (this.props.isLoading) {
      return null;
    }
    return (
      <MUITooltip title="Close">
        <MUIBox
          component="span"
          sx={{
            display: "inline-block",
            position: "absolute",
            top: 8,
            right: 8,
          }}
        >
          <MUIIconButton
            aria-label="Close"
            size="small"
            disabled={this.props.addingProducts}
            onClick={this.props.onClose}
          >
            <MUICloseIcon fontSize="small" />
          </MUIIconButton>
        </MUIBox>
      </MUITooltip>
    );
  }
  /**
   * Render main content of dialog
   */
  renderContent() {
    // const excludedProductIDs = [27, 28, 44, 52, 86, 89, 34];
    // service, pickup, recert, internal request, supply - order recurring, office supplies, loaner

    const handleProductSubTypeChange = (targetRow, productSubType) => {
      this.setState((prevState) => {
        const updatedRows = [...prevState.rows];
        const rowIndex = updatedRows.indexOf(targetRow);
        if (rowIndex === -1) {
          return null;
        }

        const currentRow = updatedRows[rowIndex];
        const nextQuantity =
          productSubType?.defaultQuantity > 0
            ? productSubType.defaultQuantity
            : currentRow.originalQuantity || 1;
        updatedRows[rowIndex] = {
          ...currentRow,
          productSubType: productSubType || null,
          qty: nextQuantity,
        };
        return { rows: updatedRows };
      });
    };

    const getProgressMessage = (progress) => {
      if (progress <= 10) {
        return "Uploading...";
      } else if (progress <= 75) {
        const order =
          this.state.aiMessageOrder.length === this.aiMessages.length
            ? this.state.aiMessageOrder
            : Array.from({ length: this.aiMessages.length }, (_, i) => i);
        return this.aiMessages[order[this.state.aiMessageIndex] || 0];
      } else {
        return "Finalizing...";
      }
    };

    if (!this.props.isLoading && !this.props.uploadQuoteData) {
      return (
        <MUIAlert severity="error" sx={{ mt: 2 }}>
          There was an error uploading the quote.
        </MUIAlert>
      );
    }

    if (this.props.isLoading) {
      return (
        <Progress
          message={getProgressMessage(this.state.progress)}
          variant="determinate"
          linear={true}
          value={Math.min(Math.round(this.state.progress), 100)}
        />
      );
    } else if (this.props.uploadQuoteData) {
      return (
        <>
          {this.state.rows?.length > 0 ? (
            <MUITableContainer component={MUIPaper} sx={{ mt: 2 }}>
              <MUITable stickyHeader size="small">
                <MUITableHead>
                  <MUITableRow>
                    <MUITableCell>Product Description</MUITableCell>
                    <MUITableCell>HCPCS</MUITableCell>
                    <MUITableCell>Qty</MUITableCell>
                    <MUITableCell>Price</MUITableCell>
                    <MUITableCell sx={{ width: "300px" }}>
                      Equipment
                    </MUITableCell>
                  </MUITableRow>
                </MUITableHead>
                <MUITableBody>
                  {[...this.state.rows]
                    .sort((a, b) => {
                      const aHasHcpcs = a.hcpcs && a.hcpcs !== "";
                      const bHasHcpcs = b.hcpcs && b.hcpcs !== "";
                      return aHasHcpcs && !bHasHcpcs
                        ? -1
                        : !aHasHcpcs && bHasHcpcs
                          ? 1
                          : 0;
                    })
                    .map((row, index) => {
                      const options = this.getProductSubTypeOptions(row, index);
                      const defaultValue =
                        options.length === 1 ? options[0] : undefined;
                      const hasQuantityOverride =
                        hasQuoteRowQuantityOverride(row);

                      if (
                        options.length === 1 &&
                        (!row.productSubType ||
                          row.productSubType.id !== options[0].value.id)
                      ) {
                        if (!row.autoSetProductSubType) {
                          row.autoSetProductSubType = true;
                          setTimeout(
                            () =>
                              handleProductSubTypeChange(row, options[0].value),
                            0,
                          );
                        }
                      }

                      if (
                        options.length === 0 &&
                        (row.productSubType === undefined ||
                          (typeof row.productSubType === "object" &&
                            Object.keys(row.productSubType).length !== 0))
                      ) {
                        if (!row.autoSetProductSubTypeEmpty) {
                          row.autoSetProductSubTypeEmpty = true;
                          setTimeout(
                            () => handleProductSubTypeChange(row, {}),
                            0,
                          );
                        }
                      }

                      return (
                        <MUITableRow
                          key={`${row.part_number || row.description || "quote-row"}-${index}`}
                        >
                          <MUITableCell>{row.description || ""}</MUITableCell>
                          <MUITableCell>
                            {row.hcpcs ? row.hcpcs.substring(0, 5) : ""}
                          </MUITableCell>
                          <MUITableCell>
                            <MUIStack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                alignItems: "center",
                              }}
                            >
                              <span>{row.qty || 1}</span>
                              {hasQuantityOverride && (
                                <MUITooltip
                                  title={`Sales Pilot default quantity (${row.qty || 1}) overrides the quote quantity (${row.originalQuantity || 1}).`}
                                >
                                  <MUIInfoOutlinedIcon
                                    fontSize="inherit"
                                    color="action"
                                  />
                                </MUITooltip>
                              )}
                            </MUIStack>
                          </MUITableCell>
                          <MUITableCell>
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(row.price)}
                          </MUITableCell>
                          <MUITableCell>
                            <MUIAutocomplete
                              options={options}
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) =>
                                option.value?.id === value?.value?.id
                              }
                              value={
                                row.productSubType?.id
                                  ? {
                                      key: row.productSubType.id,
                                      label: `[${ProductType.getDisplayName(this.context.productTypesIndexed[row.productSubType.productTypeId])}] ${row.productSubType.name}`,
                                      value: row.productSubType,
                                    }
                                  : defaultValue || null
                              }
                              onChange={(event, value) =>
                                handleProductSubTypeChange(
                                  row,
                                  value ? value.value : null,
                                )
                              }
                              renderOption={(props, option) => (
                                <li {...props} key={option.key}>
                                  {option.label}
                                </li>
                              )}
                              renderInput={(params) => {
                                return (
                                  <MUITextField
                                    {...params}
                                    size="small"
                                    variant="standard"
                                    fullWidth={true}
                                  />
                                );
                              }}
                            />
                          </MUITableCell>
                        </MUITableRow>
                      );
                    })}
                </MUITableBody>
              </MUITable>
            </MUITableContainer>
          ) : (
            <EmptyState
              line1="No products detected"
              line2="The uploaded quote was processed, but no line items were found. This can sometimes happen if the quote was scanned/faxed an uploaded instead of uploading the original version from the manufacturer."
            />
          )}
          {this.renderAlert()}
        </>
      );
    }
  }

  renderAlert() {
    return (
      <MUIAlert
        sx={{ mt: 1 }}
        severity="info"
        action={
          <MUIStack direction="row" spacing={1}>
            <MUIButton
              color="inherit"
              size="small"
              onClick={() =>
                this.setState((prev) => ({ showSource: !prev.showSource }))
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              {this.state.showSource ? "Hide Source" : "View Source"}
            </MUIButton>
            {this.state.showSource && (
              <MUIIconButton
                color="inherit"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(
                    JSON.stringify(this.props.uploadQuoteData, null, 2),
                  );
                  NotistackEnqueueSnackbar("JSON copied to clipboard!", {
                    variant: "success",
                  });
                }}
                title="Copy JSON to clipboard"
              >
                <MUICopyIcon fontSize="small" />
              </MUIIconButton>
            )}
            <MUITooltip>
              <span>
                <MUIButton
                  variant="outlined"
                  size="small"
                  loading={this.props.isLoading}
                  disabled={this.props.isLoading}
                  onClick={() => {
                    this.props.onReprocess();
                  }}
                >
                  Reprocess
                </MUIButton>
              </span>
            </MUITooltip>
          </MUIStack>
        }
      >
        {`Found ${this.state.rows?.length || 0} products in ${this.state.processingTime ? moment.duration(this.state.processingTime, "seconds").asSeconds().toFixed(2) : "0.00"}s`}
        {this.state.showSource && (
          <MUIBox sx={{ mt: 1 }}>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {JSON.stringify(this.props.uploadQuoteData, null, 2)}
            </pre>
          </MUIBox>
        )}
      </MUIAlert>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  renderActions() {
    const isDisabled = () => {
      if (!this.state.rows || this.state.rows.length === 0) {
        return true;
      }
      return this.state.rows.some((row, index) => {
        const options = this.getProductSubTypeOptions(row, index);
        return options.length > 0 && !row.productSubType;
      });
    };

    if (!this.props.isLoading && !this.props.uploadQuoteData) {
      return (
        <MUIDialogActions>
          <MUIButton variant="text" onClick={this.props.onClose}>
            Close
          </MUIButton>
        </MUIDialogActions>
      );
    }

    if (this.props.isLoading) {
      return null;
    }

    return (
      <MUIDialogActions>
        <MUIStack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginLeft: 2,
          }}
        >
          <MUIFormControlLabel
            control={
              <MUICheckbox
                checked={this.state.overwriteExisting}
                onChange={(e) =>
                  this.setState({ overwriteExisting: e.target.checked })
                }
                size="small"
              />
            }
            label="Overwrite existing equipment"
            sx={{ my: 0 }}
          />
          <MUIStack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            <MUIButton
              variant="text"
              onClick={this.props.onClose}
              disabled={this.props.addingProducts}
            >
              Cancel
            </MUIButton>
            <MUITooltip
              title={
                isDisabled()
                  ? "All products must have equipment selected before applying"
                  : ""
              }
              disableHoverListener={!isDisabled()}
            >
              <span>
                <MUIButton
                  variant="contained"
                  disabled={isDisabled()}
                  loading={this.props.addingProducts}
                  onClick={() =>
                    this.props.onConfirm(
                      this.state.rows,
                      this.state.overwriteExisting,
                    )
                  }
                >
                  Apply
                </MUIButton>
              </span>
            </MUITooltip>
          </MUIStack>
        </MUIStack>
      </MUIDialogActions>
    );
  }

  /**
   * @returns {string} The max width of the dialog.
   */
  getMaxWidth() {
    return "md";
  }

  /**
   * @returns The list of sutype options for specified row.
   */
  getProductSubTypeOptions = (row) => {
    const excludedProductTypeIds = [
      27, // Service
      28, // Pickup
      44, // Loaner
      52, // Recert
      86, // Internal Request
      89, // Supply - Order Recurring
      34, // Office Supplies
    ];

    const rowHcpcs = row.hcpcs ? row.hcpcs.substring(0, 5).toUpperCase() : null;

    let filteredOptions = [];
    let allOptions = [];

    Object.values(this.context.productSubTypesIndexed).forEach(
      (productSubType) => {
        if (excludedProductTypeIds.includes(productSubType.productTypeId)) {
          return;
        }

        const productTypeDisplayName = ProductType.getDisplayName(
          this.context.productTypesIndexed[productSubType.productTypeId],
        );
        const option = {
          key: productSubType.id,
          label: `[${productTypeDisplayName}] ${productSubType.name}`,
          value: productSubType,
        };

        allOptions.push(option);
        if (
          rowHcpcs &&
          productSubType.hcpcs?.substring(0, 5).toUpperCase() === rowHcpcs
        ) {
          filteredOptions.push(option);
        }
      },
    );

    const sortFunction = (a, b) => a.label.localeCompare(b.label);

    if (!rowHcpcs) {
      return allOptions.sort(sortFunction);
    }

    return (filteredOptions.length > 0 ? filteredOptions : allOptions).sort(
      sortFunction,
    );
  };
}
